import type { CoverPersona } from './coverQuizData';
import { L } from './quizLang';
import type { QuizLang, Txt } from './quizLang';

/**
 * 保障小測驗 · 第二部分 — 理財目標參考.
 *
 * After part 1 the client may set up to two financial targets and gets a
 * reference report: what such a target typically costs today, what it will
 * cost when they need it, roughly what monthly saving gets them there, and —
 * from the detail questions — a few reference figures and how people in the
 * same situation usually go about it.
 *
 * ⚠️ DEMO FIGURES. Every benchmark below is a typical, rounded figure for a
 * demo, not sourced data. Before release each one needs a source and a date
 * (and compliance sign-off, since the report shows a saving projection).
 *
 * Assumptions, shown to the client on the report: prices rise 3% a year, and
 * savings earn 5% a year compounded monthly. The figures are in HKD at
 * today's prices unless marked as future.
 *
 * Every goal's FIRST-ASKED questions are required for an estimate; the detail
 * questions added later fall back to a default in `estimate`, so a record
 * saved before they existed still prices the same as it did.
 */

export const DEMO_INFLATION = 0.03;
export const DEMO_RETURN = 0.05;
export const GOAL_MAX = 2;

export type GoalOption = { key: string; label: Txt };
export type GoalQuestion = { id: string; question: Txt; options: GoalOption[] };
export type GoalEstimate = {
  low: number;
  high: number;
  years: number;
  basis: Txt;
  /** How people in the same situation usually go about it. */
  typical?: Txt;
  /** Reference figures that follow from the answers. */
  facts?: Txt[];
};
/** What the rest of part 2 knows, for goals whose facts depend on it. */
export type GoalContext = { monthlyIncome?: number | null };
export type GoalDef = {
  id: string;
  icon: string;
  label: Record<CoverPersona, Txt> | Txt;
  personas: CoverPersona[];
  questions: GoalQuestion[];
  /** Null until every required question is answered. */
  estimate: (answers: Record<string, string>, context?: GoalContext) => GoalEstimate | null;
};

const WAN = 10000;

/**
 * 「約 140 萬」 for big sums in Chinese; 「1.4M」 / 「50K」 in English.
 * 「5,600」 for monthly ones in every language.
 */
export const formatWan = (value: number, lang: QuizLang) => {
  const wan = Math.round(value / WAN);
  if (lang !== 'en') return `${wan.toLocaleString()} 萬`.replace('萬', lang === 'zh' ? '万' : '萬');
  return wan >= 100 ? `${(wan / 100).toLocaleString('en', { maximumFractionDigits: 2 })}M` : `${wan * 10}K`;
};
export const formatMonthly = (value: number) => `${(Math.round(value / 100) * 100).toLocaleString()}`;

// ── shared questions ──────────────────────────────────────────────────────────
const WHEN: GoalQuestion = {
  id: 'when',
  question: L('大約幾年後需要這筆錢？', '大约几年后需要这笔钱？', 'In about how many years will you need the money?'),
  options: [
    { key: 'y2', label: L('1–3 年', '1–3 年', '1–3 years') },
    { key: 'y5', label: L('4–7 年', '4–7 年', '4–7 years') },
    { key: 'y10', label: L('8–12 年', '8–12 年', '8–12 years') },
    { key: 'y15', label: L('13 年以上', '13 年以上', '13+ years') }
  ]
};
const WHEN_YEARS: Record<string, number> = { y2: 2, y5: 5, y10: 10, y15: 15 };

const CHILD_AGE: GoalQuestion = {
  id: 'childAge',
  question: L('孩子現在幾歲？', '孩子现在几岁？', 'How old is your child now?'),
  options: [
    { key: 'a0', label: L('0–5 歲', '0–5 岁', '0–5') },
    { key: 'a6', label: L('6–11 歲', '6–11 岁', '6–11') },
    { key: 'a12', label: L('12–15 歲', '12–15 岁', '12–15') },
    { key: 'a16', label: L('16 歲或以上', '16 岁或以上', '16 or over') }
  ]
};
/** Years until 18, from the middle of each band. */
const YEARS_TO_18: Record<string, number> = { a0: 15, a6: 9, a12: 4, a16: 1 };

const OWN_AGE: GoalQuestion = {
  id: 'age',
  question: L('你現在幾歲？', '你现在几岁？', 'How old are you now?'),
  options: [
    { key: 'a25', label: L('25–34 歲', '25–34 岁', '25–34') },
    { key: 'a35', label: L('35–44 歲', '35–44 岁', '35–44') },
    { key: 'a45', label: L('45–54 歲', '45–54 岁', '45–54') },
    { key: 'a55', label: L('55 歲或以上', '55 岁或以上', '55 or over') }
  ]
};
/** The age each band is taken as. */
const AGE_NOW: Record<string, number> = { a25: 30, a35: 40, a45: 50, a55: 58 };

const HOME_PRICE: GoalQuestion = {
  id: 'price',
  question: L(
    '心目中的物業大約多少錢？',
    '心目中的物业大约多少钱？',
    'Roughly what price is the home you have in mind?'
  ),
  options: [
    { key: 'p45', label: L('500 萬以下', '500 万以下', 'Under $5M') },
    { key: 'p65', label: L('500–800 萬', '500–800 万', '$5M–8M') },
    { key: 'p100', label: L('800 萬以上', '800 万以上', 'Over $8M') }
  ]
};
const HOME_PRICES: Record<string, number> = { p45: 450 * WAN, p65: 650 * WAN, p100: 1000 * WAN };

const bandOf = (table: Record<string, [number, number]>, key?: string) => (key ? table[key] : undefined);
const scale = (band: [number, number], by: number): [number, number] => [band[0] * by, band[1] * by];
const add = (band: [number, number], extra: [number, number]): [number, number] => [
  band[0] + extra[0],
  band[1] + extra[1]
];

// ── demo benchmarks (HKD, today's prices) ────────────────────────────────────
/** Total cost of a whole programme, tuition plus living. */
const STUDY_ABROAD: Record<string, Record<string, [number, number]>> = {
  usa: { hs: [150 * WAN, 250 * WAN], ug: [140 * WAN, 250 * WAN], pg: [60 * WAN, 120 * WAN] },
  uk: { hs: [120 * WAN, 180 * WAN], ug: [100 * WAN, 135 * WAN], pg: [40 * WAN, 60 * WAN] },
  canada: { hs: [90 * WAN, 130 * WAN], ug: [115 * WAN, 160 * WAN], pg: [45 * WAN, 70 * WAN] },
  aus: { hs: [80 * WAN, 120 * WAN], ug: [90 * WAN, 110 * WAN], pg: [60 * WAN, 80 * WAN] }
};
const COUNTRY_LABEL: Record<string, Txt> = {
  usa: L('美國', '美国', 'US'),
  uk: L('英國', '英国', 'UK'),
  canada: L('加拿大', '加拿大', 'Canada'),
  aus: L('澳洲', '澳洲', 'Australia')
};
const LEVEL_LABEL: Record<string, Txt> = {
  hs: L('中學寄宿（3 年）', '中学寄宿（3 年）', 'boarding school (3 years)'),
  ug: L('本科', '本科', 'undergraduate degree'),
  pg: L('碩士', '硕士', "master's degree")
};
const COUNTRY_FACT: Record<string, Txt> = {
  usa: L(
    '美國本科一般讀 4 年，私立大學一年學費約 40–60 萬港元，公立大學的國際生學費較低',
    '美国本科一般读 4 年，私立大学一年学费约 40–60 万港元，公立大学的国际生学费较低',
    'A US undergraduate degree usually takes 4 years; private universities charge about HK$400K–600K a year, public ones less for international students'
  ),
  uk: L(
    '英國本科一般 3 年、碩士 1 年，課程較短，總開支因此較低',
    '英国本科一般 3 年、硕士 1 年，课程较短，总开支因此较低',
    "UK undergraduate degrees usually take 3 years and master's 1 year, so shorter courses keep the total lower"
  ),
  canada: L(
    '加拿大國際生學費每年約 20–35 萬港元，各省及院校差距頗大',
    '加拿大国际生学费每年约 20–35 万港元，各省及院校差距颇大',
    'International tuition in Canada is about HK$200K–350K a year, varying widely by province and school'
  ),
  aus: L(
    '澳洲本科一般 3 年，國際生學費每年約 20–30 萬港元',
    '澳洲本科一般 3 年，国际生学费每年约 20–30 万港元',
    'An Australian undergraduate degree usually takes 3 years, with international tuition about HK$200K–300K a year'
  )
};

/** 「兩成」, not 「2成」 — the Chinese share words; English uses a percentage. */
const CHENG: Record<number, Txt> = {
  1: L('一成', '一成', '10%'),
  2: L('兩成', '两成', '20%'),
  3: L('三成', '三成', '30%'),
  7: L('七成', '七成', '70%'),
  8: L('八成', '八成', '80%'),
  9: L('九成', '九成', '90%')
};

/** A repayment on a 30-year mortgage at about 3.5%. */
const MORTGAGE_RATE = 0.035;
const mortgageMonthly = (loan: number) => {
  const r = MORTGAGE_RATE / 12;
  const n = 30 * 12;
  return (loan * r) / (1 - Math.pow(1 + r, -n));
};

export const GOALS: GoalDef[] = [
  {
    id: 'abroad',
    icon: '🎓',
    label: L('子女海外升學', '子女海外升学', 'Studying abroad (children)'),
    personas: ['parent'],
    questions: [
      {
        id: 'country',
        question: L('想去哪裏讀書？', '想去哪里读书？', 'Where would they study?'),
        options: [
          { key: 'usa', label: COUNTRY_LABEL.usa },
          { key: 'uk', label: COUNTRY_LABEL.uk },
          { key: 'canada', label: COUNTRY_LABEL.canada },
          { key: 'aus', label: COUNTRY_LABEL.aus }
        ]
      },
      {
        id: 'level',
        question: L('從哪個階段開始出國？', '从哪个阶段开始出国？', 'From which stage would they go abroad?'),
        options: [
          { key: 'ug', label: L('大學本科', '大学本科', 'Undergraduate') },
          { key: 'pg', label: L('碩士', '硕士', "Master's") },
          { key: 'hs', label: L('中學寄宿', '中学寄宿', 'Boarding school') }
        ]
      },
      CHILD_AGE,
      {
        id: 'stay',
        question: L('在當地住哪裏？', '在当地住哪里？', 'Where would they live?'),
        options: [
          { key: 'dorm', label: L('學校宿舍', '学校宿舍', 'School residence') },
          { key: 'rent', label: L('校外租屋', '校外租屋', 'Rented flat') }
        ]
      }
    ],
    estimate: a => {
      const band = STUDY_ABROAD[a.country]?.[a.level];
      const toEighteen = YEARS_TO_18[a.childAge];
      if (!band || toEighteen === undefined) return null;
      // A master's starts around 22, four years after an undergraduate degree
      // does; boarding school around 15, three years before.
      const years = Math.max(1, toEighteen + (a.level === 'pg' ? 4 : a.level === 'hs' ? -3 : 0));
      const [low, high] = a.stay === 'rent' ? scale(band, 1.1) : band;
      const facts = [
        COUNTRY_FACT[a.country],
        L(
          '學費以外幣計算，匯率變動會令實際開支增減',
          '学费以外币计算，汇率变动会令实际开支增减',
          'Fees are charged in foreign currency, so exchange rates move the actual cost'
        )
      ];
      if (a.stay === 'rent') {
        facts.push(
          L(
            '校外租屋通常比宿舍貴一成或以上，還要預留按金和傢俬',
            '校外租屋通常比宿舍贵一成或以上，还要预留按金和家私',
            'Renting off campus usually costs 10% or more than a residence, plus a deposit and furniture'
          )
        );
      }
      const country = COUNTRY_LABEL[a.country];
      const level = LEVEL_LABEL[a.level];
      return {
        low,
        high,
        years,
        basis: L(
          `${country['zh-Hant-HK']}${level['zh-Hant-HK']}整個課程的學費及生活費`,
          `${country.zh}${level.zh}整个课程的学费及生活费`,
          `Tuition and living costs for a whole ${country.en} ${level.en}`
        ),
        typical:
          a.level === 'hs'
            ? L(
                '中學寄宿的年期較長，除學費外，假期回港的機票和監護人費用也要計算在內。',
                '中学寄宿的年期较长，除学费外，假期回港的机票和监护人费用也要计算在内。',
                'Boarding school runs for longer; besides fees, count flights home in the holidays and guardian fees.'
              )
            : L(
                '不少家長在孩子讀小學時已開始為海外升學儲蓄，並預留匯率變動的空間。',
                '不少家长在孩子读小学时已开始为海外升学储蓄，并预留汇率变动的空间。',
                'Many parents start saving for overseas study while their child is in primary school, leaving room for exchange-rate swings.'
              ),
        facts
      };
    }
  },
  {
    id: 'localUni',
    icon: '🏫',
    label: L('子女本地大學', '子女本地大学', 'Local university (children)'),
    personas: ['parent'],
    questions: [
      CHILD_AGE,
      {
        id: 'stay',
        question: L('讀大學時住哪裏？', '读大学时住哪里？', 'Where would they live at university?'),
        options: [
          { key: 'home', label: L('住家', '住家', 'At home') },
          { key: 'hall', label: L('住宿舍', '住宿舍', 'In a residence hall') }
        ]
      }
    ],
    estimate: a => {
      const years = YEARS_TO_18[a.childAge];
      if (years === undefined) return null;
      const hall = a.stay === 'hall';
      const [low, high] = hall ? add([25 * WAN, 40 * WAN], [6 * WAN, 12 * WAN]) : [25 * WAN, 40 * WAN];
      const facts = [
        L(
          '本地資助學士學位課程學費約每年 4.5 萬港元',
          '本地资助学士学位课程学费约每年 4.5 万港元',
          'Tuition for a local government-funded degree is about HK$45K a year'
        )
      ];
      if (hall) {
        facts.push(
          L(
            '大學宿舍每年約 1.5–3 萬港元，未計膳食',
            '大学宿舍每年约 1.5–3 万港元，未计膳食',
            'University halls cost about HK$15K–30K a year, meals extra'
          )
        );
      }
      return {
        low,
        high,
        years,
        basis: hall
          ? L(
              '本地大學 4 年學費、生活費及宿費',
              '本地大学 4 年学费、生活费及宿费',
              '4 years of local university tuition, living costs and hall fees'
            )
          : L(
              '本地大學 4 年學費及生活費',
              '本地大学 4 年学费及生活费',
              '4 years of local university tuition and living costs'
            ),
        typical: L(
          '除學費外，書簿、交通、交流團和實習往往是容易忽略的開支。',
          '除学费外，书簿、交通、交流团和实习往往是容易忽略的开支。',
          'Beyond tuition, books, transport, exchange trips and internships are easy-to-miss costs.'
        ),
        facts
      };
    }
  },
  {
    id: 'home',
    icon: '🏠',
    label: {
      parent: L('換樓首期', '换楼首期', 'Down payment to move home'),
      individual: L('置業首期', '置业首期', 'Down payment for a home')
    },
    personas: ['parent', 'individual'],
    questions: [
      HOME_PRICE,
      WHEN,
      {
        id: 'down',
        question: L('打算付幾成首期？', '打算付几成首期？', 'How much of a down payment?'),
        options: [
          { key: 'd10', label: L('一成', '一成', '10%') },
          { key: 'd20', label: L('兩成', '两成', '20%') },
          { key: 'd30', label: L('三成或以上', '三成或以上', '30% or more') }
        ]
      }
    ],
    estimate: (a, context) => {
      const price = HOME_PRICES[a.price];
      if (!price || WHEN_YEARS[a.when] === undefined) return null;
      const down = ({ d10: 0.1, d20: 0.2, d30: 0.3 } as Record<string, number>)[a.down];
      // The down payment plus about 3–5% for stamp duty and other costs; without
      // a chosen share, the old 20–30% band.
      const [low, high] = down ? [price * (down + 0.03), price * (down + 0.05)] : [price * 0.24, price * 0.34];
      const facts: Txt[] = [];
      if (down) {
        const repayment = formatMonthly(mortgageMonthly(price * (1 - down)));
        const loan = CHENG[Math.round((1 - down) * 10)];
        facts.push(
          L(
            `借${loan['zh-Hant-HK']}、30 年、年利率約 3.5% 計，每月供款約 HK$ ${repayment}`,
            `借${loan.zh}、30 年、年利率约 3.5% 计，每月供款约 HK$ ${repayment}`,
            `Borrowing ${loan.en} over 30 years at about 3.5% a year, the monthly repayment is about HK$ ${repayment}`
          )
        );
        const income = context?.monthlyIncome;
        if (income) {
          const share = Math.round((mortgageMonthly(price * (1 - down)) / income) * 100);
          facts.push(
            L(
              `約佔你每月收入的 ${share}%（銀行一般要求供款不超過月入一半）`,
              `约占你每月收入的 ${share}%（银行一般要求供款不超过月入一半）`,
              `About ${share}% of your monthly income (banks usually cap repayments at half of monthly income)`
            )
          );
        }
      }
      if (a.down === 'd10') {
        facts.push(
          L(
            '一成首期通常要配合按揭保險，只適用於指定樓價以下的物業',
            '一成首期通常要配合按揭保险，只适用于指定楼价以下的物业',
            'A 10% down payment usually needs mortgage insurance and only applies below a set property price'
          )
        );
      }
      const share = down ? CHENG[Math.round(down * 10)] : null;
      return {
        low,
        high,
        years: WHEN_YEARS[a.when],
        basis: share
          ? L(
              `${share['zh-Hant-HK']}首期，另加約 3–5% 印花稅及雜費`,
              `${share.zh}首期，另加约 3–5% 印花税及杂费`,
              `A ${share.en} down payment, plus about 3–5% stamp duty and fees`
            )
          : L(
              '兩至三成首期，另加約 4% 印花稅及雜費',
              '两至三成首期，另加约 4% 印花税及杂费',
              'A 20–30% down payment, plus about 4% stamp duty and fees'
            ),
        typical: L(
          '置業前除了首期，也要預留裝修、傢俬和搬遷的開支，以及供樓期間的應急錢。',
          '置业前除了首期，也要预留装修、家私和搬迁的开支，以及供楼期间的应急钱。',
          'Besides the down payment, set aside money for renovation, furniture and moving, and an emergency fund while repaying.'
        ),
        facts
      };
    }
  },
  {
    id: 'retire',
    icon: '🌅',
    label: L('退休生活', '退休生活', 'Retirement'),
    personas: ['parent', 'individual'],
    questions: [
      OWN_AGE,
      {
        id: 'spend',
        question: L(
          '退休後每月想用多少（以今天的物價計）？',
          '退休后每月想用多少（以今天的物价计）？',
          "How much a month would you like to spend in retirement (at today's prices)?"
        ),
        options: [
          { key: 'm15', label: L('約 1.5 萬', '约 1.5 万', 'About $15K') },
          { key: 'm25', label: L('約 2.5 萬', '约 2.5 万', 'About $25K') },
          { key: 'm40', label: L('約 4 萬或以上', '约 4 万或以上', 'About $40K or more') }
        ]
      },
      {
        id: 'retireAge',
        question: L('想幾歲退休？', '想几岁退休？', 'At what age would you like to retire?'),
        options: [
          { key: 'r55', label: L('55 歲', '55 岁', '55') },
          { key: 'r60', label: L('60 歲', '60 岁', '60') },
          { key: 'r65', label: L('65 歲', '65 岁', '65') }
        ]
      }
    ],
    estimate: a => {
      const monthly = ({ m15: 1.5, m25: 2.5, m40: 4 } as Record<string, number>)[a.spend];
      const now = AGE_NOW[a.age];
      if (!monthly || now === undefined) return null;
      const retireAt = ({ r55: 55, r60: 60, r65: 65 } as Record<string, number>)[a.retireAge] || 65;
      // Living to 85–90.
      const shortest = 85 - retireAt;
      const longest = 90 - retireAt;
      return {
        low: monthly * WAN * 12 * shortest,
        high: monthly * WAN * 12 * longest,
        years: Math.max(1, retireAt - now),
        basis: L(
          `${retireAt} 歲退休後 ${shortest}–${longest} 年的生活費`,
          `${retireAt} 岁退休后 ${shortest}–${longest} 年的生活费`,
          `${shortest}–${longest} years of living costs after retiring at ${retireAt}`
        ),
        typical:
          retireAt < 65
            ? L(
                '越早退休，要準備的年期越長，工作儲錢的時間卻越短，每月要存的也就越多。',
                '越早退休，要准备的年期越长，工作储钱的时间却越短，每月要存的也就越多。',
                'The earlier you retire, the longer the money must last and the shorter the time to save, so the more each month.'
              )
            : L(
                '很多人會結合強積金、儲蓄和其他收入來源一起準備退休。',
                '很多人会结合强积金、储蓄和其他收入来源一起准备退休。',
                'Many people combine their MPF, savings and other income sources to prepare for retirement.'
              ),
        facts: [
          L(
            '香港人預期壽命超過 80 歲，退休生活可長達 20–30 年',
            '香港人预期寿命超过 80 岁，退休生活可长达 20–30 年',
            'Life expectancy in Hong Kong is over 80, so retirement can last 20–30 years'
          ),
          L(
            '強積金僱員供款上限為每月 1,500 元，單靠強積金一般不足以支持退休開支',
            '强积金雇员供款上限为每月 1,500 元，单靠强积金一般不足以支持退休开支',
            "Employee MPF contributions are capped at $1,500 a month, so the MPF alone usually can't fund retirement"
          )
        ]
      };
    }
  },
  {
    id: 'emergency',
    icon: '🚑',
    label: L('家庭應急錢', '家庭应急钱', 'Family emergency fund'),
    personas: ['parent'],
    questions: [
      {
        id: 'expense',
        question: L(
          '家庭每月開支大約多少？',
          '家庭每月开支大约多少？',
          'Roughly how much does the family spend a month?'
        ),
        options: [
          { key: 'e20', label: L('約 2 萬', '约 2 万', 'About $20K') },
          { key: 'e35', label: L('約 3.5 萬', '约 3.5 万', 'About $35K') },
          { key: 'e50', label: L('約 5 萬或以上', '约 5 万或以上', 'About $50K or more') }
        ]
      },
      {
        id: 'earners',
        question: L('家中有幾多人有收入？', '家中有几多人有收入？', 'How many people in the family earn an income?'),
        options: [
          { key: 'one', label: L('一人', '一人', 'One') },
          { key: 'two', label: L('兩人或以上', '两人或以上', 'Two or more') }
        ]
      }
    ],
    estimate: a => {
      const monthly = ({ e20: 2, e35: 3.5, e50: 5 } as Record<string, number>)[a.expense];
      if (!monthly) return null;
      const [fewest, most] = a.earners === 'one' ? [6, 9] : [3, 6];
      const facts = [
        L(
          '應急錢宜放在隨時可以提取的地方，例如儲蓄戶口',
          '应急钱宜放在随时可以提取的地方，例如储蓄户口',
          'Keep an emergency fund somewhere you can withdraw any time, such as a savings account'
        )
      ];
      if (a.earners === 'one') {
        facts.push(
          L(
            '單一收入的家庭，收入一旦中斷影響較大，一般建議預留 6 個月或以上',
            '单一收入的家庭，收入一旦中断影响较大，一般建议预留 6 个月或以上',
            'A single-income family is hit harder if that income stops; 6 months or more is usually suggested'
          )
        );
      }
      return {
        low: monthly * WAN * fewest,
        high: monthly * WAN * most,
        years: 1,
        basis: L(
          `${fewest}–${most} 個月的家庭開支`,
          `${fewest}–${most} 个月的家庭开支`,
          `${fewest}–${most} months of family spending`
        ),
        typical: L(
          '應急錢是其他目標的基礎：先準備好，投資時遇到市況波動也不用急於套現。',
          '应急钱是其他目标的基础：先准备好，投资时遇到市况波动也不用急于套现。',
          "An emergency fund underpins every other goal: with it in place, market swings won't force you to sell."
        ),
        facts
      };
    }
  },
  {
    id: 'familyTrip',
    icon: '✈️',
    label: L('一家人旅行基金', '一家人旅行基金', 'Family travel fund'),
    personas: ['parent'],
    questions: [
      {
        id: 'trip',
        question: L('想去怎樣的旅行？', '想去怎样的旅行？', 'What kind of trip?'),
        options: [
          { key: 'asia', label: L('亞洲短途', '亚洲短途', 'Short trip in Asia') },
          { key: 'long', label: L('歐美長途', '欧美长途', 'Long-haul to Europe or America') }
        ]
      },
      WHEN,
      {
        id: 'size',
        question: L('一家幾口？', '一家几口？', 'How many in the family?'),
        options: [
          { key: 's3', label: L('三人', '三人', 'Three') },
          { key: 's4', label: L('四人', '四人', 'Four') },
          { key: 's5', label: L('五人或以上', '五人或以上', 'Five or more') }
        ]
      }
    ],
    estimate: a => {
      // Per person, one trip.
      const band = bandOf({ asia: [0.75 * WAN, 1.25 * WAN], long: [2 * WAN, 3.75 * WAN] }, a.trip);
      if (!band || WHEN_YEARS[a.when] === undefined) return null;
      const people = ({ s3: 3, s4: 4, s5: 5 } as Record<string, number>)[a.size] || 4;
      const [low, high] = scale(band, people);
      return {
        low,
        high,
        years: WHEN_YEARS[a.when],
        basis: L(
          `一家 ${people} 口一次旅行的機票、住宿及開支`,
          `一家 ${people} 口一次旅行的机票、住宿及开支`,
          `Flights, hotels and spending for one trip for a family of ${people}`
        ),
        typical: L(
          '不少家庭每月撥一小筆錢入旅行基金，出發時就不用動用其他儲蓄。',
          '不少家庭每月拨一小笔钱入旅行基金，出发时就不用动用其他储蓄。',
          "Many families put a little into a travel fund each month, so the trip doesn't touch other savings."
        ),
        facts: [
          L(
            '學校假期出發，機票和酒店通常較貴',
            '学校假期出发，机票和酒店通常较贵',
            'Flights and hotels usually cost more in school holidays'
          ),
          L('記得預留旅遊保障的開支', '记得预留旅游保障的开支', 'Remember to budget for travel insurance')
        ]
      };
    }
  },
  {
    id: 'business',
    icon: '🚀',
    label: L('創業資金', '创业资金', 'Business start-up fund'),
    personas: ['individual'],
    questions: [
      {
        id: 'type',
        question: L('想做哪一類生意？', '想做哪一类生意？', 'What kind of business?'),
        options: [
          { key: 'online', label: L('網店／小本生意', '网店／小本生意', 'Online shop / small business') },
          { key: 'shop', label: L('實體店／餐飲', '实体店／餐饮', 'Shop / restaurant') },
          { key: 'tech', label: L('科技初創', '科技初创', 'Tech start-up') }
        ]
      },
      WHEN,
      {
        id: 'mode',
        question: L('打算怎樣開始？', '打算怎样开始？', 'How would you start?'),
        options: [
          { key: 'part', label: L('先兼職試做', '先兼职试做', 'Part-time first') },
          { key: 'full', label: L('辭職全職做', '辞职全职做', 'Quit and go full-time') }
        ]
      }
    ],
    estimate: a => {
      const band = bandOf(
        { online: [10 * WAN, 30 * WAN], shop: [50 * WAN, 150 * WAN], tech: [100 * WAN, 300 * WAN] },
        a.type
      );
      if (!band || WHEN_YEARS[a.when] === undefined) return null;
      const full = a.mode === 'full';
      // Full time: six to twelve months of living costs, at about 2 萬 a month.
      const [low, high] = full ? add(band, [12 * WAN, 24 * WAN]) : band;
      const facts = [
        (
          {
            online: L(
              '網店的主要開支是存貨、平台費和宣傳',
              '网店的主要开支是存货、平台费和宣传',
              "An online shop's main costs are stock, platform fees and marketing"
            ),
            shop: L(
              '實體店的租金、裝修和按金通常佔開業資金的一大部分',
              '实体店的租金、装修和按金通常占开业资金的一大部分',
              'For a shop, rent, fit-out and deposits usually take a large part of the start-up money'
            ),
            tech: L(
              '科技初創往往需要一段較長時間才有穩定收入',
              '科技初创往往需要一段较长时间才有稳定收入',
              'A tech start-up often takes a long while to earn a steady income'
            )
          } as Record<string, Txt>
        )[a.type]
      ];
      if (full) {
        facts.push(
          L(
            '全職創業初期未必有收入，已另加 6–12 個月的生活費',
            '全职创业初期未必有收入，已另加 6–12 个月的生活费',
            'A full-time founder may earn nothing at first, so 6–12 months of living costs are added'
          )
        );
      }
      return {
        low,
        high,
        years: WHEN_YEARS[a.when],
        basis: full
          ? L(
              '開業資金、首半年營運開支及生活費',
              '开业资金、首半年营运开支及生活费',
              'Start-up money, the first six months of running costs, and living costs'
            )
          : L(
              '開業資金及首半年營運開支',
              '开业资金及首半年营运开支',
              'Start-up money and the first six months of running costs'
            ),
        typical: L(
          '創業後不再有公司提供的醫療保障和強積金供款，自己的保障要另外安排。',
          '创业后不再有公司提供的医疗保障和强积金供款，自己的保障要另外安排。',
          'Running your own business means no employer medical cover or MPF contributions, so arrange your own cover.'
        ),
        facts
      };
    }
  },
  {
    id: 'study',
    icon: '📚',
    label: L('進修', '进修', 'Further studies'),
    personas: ['individual'],
    questions: [
      {
        id: 'type',
        question: L('想讀甚麼？', '想读什么？', 'What would you study?'),
        options: [
          { key: 'course', label: L('專業課程／證書', '专业课程／证书', 'Professional course / certificate') },
          { key: 'localPg', label: L('本地碩士', '本地硕士', "Local master's") },
          { key: 'abroadPg', label: L('海外碩士', '海外硕士', "Overseas master's") }
        ]
      },
      WHEN,
      {
        id: 'mode',
        question: L('全職還是兼讀？', '全职还是兼读？', 'Full-time or part-time?'),
        options: [
          { key: 'part', label: L('兼讀', '兼读', 'Part-time') },
          { key: 'full', label: L('全職', '全职', 'Full-time') }
        ]
      }
    ],
    estimate: a => {
      const band = bandOf(
        { course: [3 * WAN, 10 * WAN], localPg: [15 * WAN, 30 * WAN], abroadPg: [50 * WAN, 120 * WAN] },
        a.type
      );
      if (!band || WHEN_YEARS[a.when] === undefined) return null;
      const facts: Txt[] = [];
      if (a.type === 'course') {
        facts.push(
          L(
            '持續進修基金每人資助上限為 2.5 萬元，只適用於指定課程',
            '持续进修基金每人资助上限为 2.5 万元，只适用于指定课程',
            'The Continuing Education Fund subsidises up to $25K per person, for approved courses only'
          )
        );
      }
      if (a.mode === 'full') {
        facts.push(
          L(
            '全職進修期間沒有收入，記得另外預留生活費',
            '全职进修期间没有收入，记得另外预留生活费',
            'Full-time study means no income, so set aside living costs too'
          )
        );
      }
      if (a.mode === 'part') {
        facts.push(
          L(
            '兼讀可以邊工作邊讀，但要留意時間和精神上的負擔',
            '兼读可以边工作边读，但要留意时间和精神上的负担',
            'Part-time study lets you keep working, but mind the load on your time and energy'
          )
        );
      }
      return {
        low: band[0],
        high: band[1],
        years: WHEN_YEARS[a.when],
        basis: L(
          '學費，海外課程另計生活費',
          '学费，海外课程另计生活费',
          'Tuition, plus living costs for overseas courses'
        ),
        typical: L(
          '不少人選擇兼讀，一邊工作一邊進修，收入不會中斷。',
          '不少人选择兼读，一边工作一边进修，收入不会中断。',
          'Many people study part-time while working, so their income never stops.'
        ),
        facts
      };
    }
  },
  {
    id: 'wedding',
    icon: '💍',
    label: L('結婚', '结婚', 'Wedding'),
    personas: ['individual'],
    questions: [
      {
        id: 'style',
        question: L('心目中的婚禮是？', '心目中的婚礼是？', 'What kind of wedding do you have in mind?'),
        options: [
          { key: 'simple', label: L('簡約', '简约', 'Simple') },
          { key: 'classic', label: L('傳統酒席', '传统酒席', 'Traditional banquet') },
          { key: 'grand', label: L('隆重', '隆重', 'Grand') }
        ]
      },
      WHEN,
      {
        id: 'honeymoon',
        question: L('蜜月想去哪裏？', '蜜月想去哪里？', 'Where for the honeymoon?'),
        options: [
          { key: 'none', label: L('暫時不去', '暂时不去', 'Not for now') },
          { key: 'asia', label: L('亞洲', '亚洲', 'Asia') },
          { key: 'long', label: L('歐美長途', '欧美长途', 'Long-haul to Europe or America') }
        ]
      }
    ],
    estimate: a => {
      const band = bandOf(
        { simple: [10 * WAN, 20 * WAN], classic: [30 * WAN, 50 * WAN], grand: [60 * WAN, 100 * WAN] },
        a.style
      );
      if (!band || WHEN_YEARS[a.when] === undefined) return null;
      const honeymoon = bandOf({ asia: [2 * WAN, 4 * WAN], long: [5 * WAN, 10 * WAN] }, a.honeymoon);
      const [low, high] = honeymoon ? add(band, honeymoon) : band;
      const facts: Txt[] = [];
      if (a.style !== 'simple') {
        facts.push(
          L(
            '酒席開支隨圍數增加，禮金可以抵銷其中一部分',
            '酒席开支随围数增加，礼金可以抵销其中一部分',
            'Banquet costs grow with the number of tables; gift money can offset part of it'
          )
        );
      }
      return {
        low,
        high,
        years: WHEN_YEARS[a.when],
        basis: honeymoon
          ? L(
              '婚宴、婚紗攝影、蜜月及相關開支',
              '婚宴、婚纱摄影、蜜月及相关开支',
              'Banquet, wedding photos, honeymoon and related costs'
            )
          : L('婚宴、婚紗攝影及相關開支', '婚宴、婚纱摄影及相关开支', 'Banquet, wedding photos and related costs'),
        typical: L(
          '不少新人會另外預留一筆錢作婚後安家，例如傢俬和裝修。',
          '不少新人会另外预留一笔钱作婚后安家，例如家私和装修。',
          'Many couples also set aside money to set up home afterwards, such as furniture and renovation.'
        ),
        facts
      };
    }
  },
  {
    id: 'travel',
    icon: '🌍',
    label: L('環遊世界／間隔年', '环游世界／间隔年', 'World trip / gap year'),
    personas: ['individual'],
    questions: [
      {
        id: 'length',
        question: L('想去多久？', '想去多久？', 'For how long?'),
        options: [
          { key: 'm3', label: L('約 3 個月', '约 3 个月', 'About 3 months') },
          { key: 'y1', label: L('約一年', '约一年', 'About a year') }
        ]
      },
      WHEN,
      {
        id: 'style',
        question: L('旅行風格？', '旅行风格？', 'Travel style?'),
        options: [
          { key: 'budget', label: L('背包客', '背包客', 'Backpacker') },
          { key: 'comfort', label: L('舒適為主', '舒适为主', 'Comfort first') }
        ]
      }
    ],
    estimate: a => {
      const band = bandOf({ m3: [8 * WAN, 15 * WAN], y1: [20 * WAN, 40 * WAN] }, a.length);
      if (!band || WHEN_YEARS[a.when] === undefined) return null;
      const [low, high] = a.style === 'comfort' ? scale(band, 1.5) : band;
      return {
        low,
        high,
        years: WHEN_YEARS[a.when],
        basis: L(
          '交通、住宿及旅途生活費',
          '交通、住宿及旅途生活费',
          'Transport, accommodation and living costs on the road'
        ),
        typical: L(
          '間隔年的開支除了旅費，還有回港後重新找工作的空檔期。',
          '间隔年的开支除了旅费，还有回港后重新找工作的空档期。',
          'Besides travel costs, a gap year includes the time between jobs after coming home.'
        ),
        facts: [
          L(
            '出發前記得預留回港後幾個月的生活費',
            '出发前记得预留回港后几个月的生活费',
            'Before leaving, set aside a few months of living costs for your return'
          ),
          L(
            '長途旅行要安排好醫療和旅遊保障',
            '长途旅行要安排好医疗和旅游保障',
            'Arrange medical and travel cover for a long trip'
          )
        ]
      };
    }
  },
  {
    id: 'kidsStart',
    icon: '🎁',
    label: L('子女起步基金', '子女起步基金', "Children's head-start fund"),
    personas: ['parent'],
    questions: [
      {
        id: 'amount',
        question: L(
          '希望給孩子多少起步資金？',
          '希望给孩子多少起步资金？',
          'How much would you like to give your child as a head start?'
        ),
        options: [
          { key: 'small', label: L('20–50 萬', '20–50 万', '$200K–500K') },
          { key: 'mid', label: L('50–100 萬', '50–100 万', '$500K–1M') },
          { key: 'large', label: L('100 萬以上', '100 万以上', 'Over $1M') }
        ]
      },
      CHILD_AGE,
      {
        id: 'purpose',
        question: L('主要想用在甚麼地方？', '主要想用在什么地方？', 'What is it mainly for?'),
        options: [
          { key: 'home', label: L('置業首期', '置业首期', 'A home down payment') },
          { key: 'business', label: L('創業', '创业', 'Starting a business') },
          { key: 'study', label: L('進修', '进修', 'Further studies') }
        ]
      }
    ],
    estimate: a => {
      const band = bandOf(
        { small: [20 * WAN, 50 * WAN], mid: [50 * WAN, 100 * WAN], large: [100 * WAN, 200 * WAN] },
        a.amount
      );
      const toEighteen = YEARS_TO_18[a.childAge];
      if (!band || toEighteen === undefined) return null;
      const purpose = (
        {
          home: L('置業首期', '置业首期', 'home down payment'),
          business: L('創業', '创业', 'business'),
          study: L('進修', '进修', 'further studies')
        } as Record<string, Txt>
      )[a.purpose];
      // Handed over around 25, seven years after 18.
      return {
        low: band[0],
        high: band[1],
        years: toEighteen + 7,
        basis: purpose
          ? L(
              `孩子約 25 歲時的一筆${purpose['zh-Hant-HK']}資金`,
              `孩子约 25 岁时的一笔${purpose.zh}资金`,
              `A ${purpose.en} fund for your child at about 25`
            )
          : L(
              '孩子約 25 歲時的一筆起步資金',
              '孩子约 25 岁时的一笔起步资金',
              'A head-start fund for your child at about 25'
            ),
        typical: L(
          '起步基金的年期長，越早開始，複利的時間越長，每月要存的就越少。',
          '起步基金的年期长，越早开始，复利的时间越长，每月要存的就越少。',
          'A head-start fund runs for years: the earlier you start, the longer compounding works and the less each month.'
        ),
        facts:
          a.purpose === 'home'
            ? [
                L(
                  '以 500 萬物業計，兩成首期連雜費約 120 萬',
                  '以 500 万物业计，两成首期连杂费约 120 万',
                  'For a $5M home, a 20% down payment with fees is about $1.2M'
                )
              ]
            : []
      };
    }
  },
  {
    id: 'parentsCare',
    icon: '👴',
    label: L('照顧父母', '照顾父母', 'Caring for parents'),
    personas: ['parent', 'individual'],
    questions: [
      {
        id: 'monthly',
        question: L(
          '每月大約會用多少照顧父母（以今天的物價計）？',
          '每月大约会用多少照顾父母（以今天的物价计）？',
          "Roughly how much a month would go on caring for your parents (at today's prices)?"
        ),
        options: [
          { key: 'm5', label: L('約 5 千', '约 5 千', 'About $5K') },
          { key: 'm10', label: L('約 1 萬', '约 1 万', 'About $10K') },
          { key: 'm20', label: L('約 2 萬（包括院舍）', '约 2 万（包括院舍）', 'About $20K (incl. a care home)') }
        ]
      },
      WHEN,
      {
        id: 'parentsAge',
        question: L('父母現在大約幾歲？', '父母现在大约几岁？', 'Roughly how old are your parents now?'),
        options: [
          { key: 'p60', label: L('60 歲以下', '60 岁以下', 'Under 60') },
          { key: 'p70', label: L('60–74 歲', '60–74 岁', '60–74') },
          { key: 'p75', label: L('75 歲或以上', '75 岁或以上', '75 or over') }
        ]
      },
      {
        id: 'share',
        question: L('開支由誰負擔？', '开支由谁负担？', 'Who pays?'),
        options: [
          { key: 'alone', label: L('主要由我負擔', '主要由我负担', 'Mainly me') },
          { key: 'share', label: L('兄弟姊妹一起分擔', '兄弟姊妹一起分担', 'Shared with siblings') }
        ]
      }
    ],
    estimate: a => {
      const monthly = ({ m5: 0.5, m10: 1, m20: 2 } as Record<string, number>)[a.monthly];
      if (!monthly || WHEN_YEARS[a.when] === undefined) return null;
      const [fewest, most] = bandOf({ p60: [10, 15], p70: [8, 12], p75: [5, 8] }, a.parentsAge) || [8, 12];
      const share = a.share === 'share' ? 0.5 : 1;
      const facts = [
        L(
          '長者的醫療開支隨年齡上升，住院和長期護理是最大的開支',
          '长者的医疗开支随年龄上升，住院和长期护理是最大的开支',
          'Medical costs rise with age; hospital stays and long-term care are the biggest'
        )
      ];
      if (a.parentsAge === 'p75') {
        facts.push(
          L(
            '私營安老院每月收費一般由 1 萬多元起',
            '私营安老院每月收费一般由 1 万多元起',
            'Private care homes usually start from just over $10K a month'
          )
        );
      }
      if (a.share === 'share') {
        facts.push(
          L(
            '已按兄弟姊妹平均分擔計算你的一份',
            '已按兄弟姊妹平均分担计算你的一份',
            'Your share assumes siblings split it evenly'
          )
        );
      }
      return {
        low: monthly * WAN * 12 * fewest * share,
        high: monthly * WAN * 12 * most * share,
        years: WHEN_YEARS[a.when],
        basis: L(
          `${fewest}–${most} 年的生活及照顧開支`,
          `${fewest}–${most} 年的生活及照顾开支`,
          `${fewest}–${most} years of living and care costs`
        ),
        typical: L(
          '不少家庭由兄弟姊妹一起分擔，事前談好各自的比例，可以減少日後的誤會。',
          '不少家庭由兄弟姊妹一起分担，事前谈好各自的比例，可以减少日后的误会。',
          'Many families share it among siblings; agreeing the split up front avoids misunderstandings later.'
        ),
        facts
      };
    }
  },
  {
    id: 'medical',
    icon: '🏥',
    label: L('醫療儲備', '医疗储备', 'Medical reserve'),
    personas: ['parent', 'individual'],
    questions: [
      {
        id: 'level',
        question: L(
          '希望預留應付哪一類醫療開支？',
          '希望预留应付哪一类医疗开支？',
          'What kind of medical costs would you like to be ready for?'
        ),
        options: [
          {
            key: 'basic',
            label: L('公立為主，偶爾看私家', '公立为主，偶尔看私家', 'Mostly public, sometimes private')
          },
          { key: 'private', label: L('私家醫院一般手術', '私家医院一般手术', 'Routine surgery at a private hospital') },
          { key: 'serious', label: L('包括重病治療', '包括重病治疗', 'Including treatment for serious illness') }
        ]
      },
      WHEN,
      {
        id: 'cover',
        question: L(
          '現時的醫療保障主要來自？',
          '现时的医疗保障主要来自？',
          'Where does your medical cover mainly come from now?'
        ),
        options: [
          { key: 'company', label: L('公司醫療福利', '公司医疗福利', 'Employer medical benefits') },
          { key: 'own', label: L('自己的保單', '自己的保单', 'My own policy') },
          { key: 'none', label: L('暫時沒有', '暂时没有', 'None for now') }
        ]
      }
    ],
    estimate: a => {
      const band = bandOf(
        { basic: [5 * WAN, 10 * WAN], private: [20 * WAN, 50 * WAN], serious: [50 * WAN, 100 * WAN] },
        a.level
      );
      if (!band || WHEN_YEARS[a.when] === undefined) return null;
      const facts: Txt[] = [];
      if (a.cover === 'company') {
        facts.push(
          L(
            '公司醫療福利一般在離職或退休後終止',
            '公司医疗福利一般在离职或退休后终止',
            'Employer medical benefits usually end when you leave or retire'
          )
        );
      }
      if (a.cover === 'none' || a.level === 'basic') {
        facts.push(
          L(
            '公立醫院收費較低，但非緊急手術的輪候時間可以很長',
            '公立医院收费较低，但非紧急手术的轮候时间可以很长',
            'Public hospitals cost less, but waits for non-urgent surgery can be long'
          )
        );
      }
      if (a.level === 'serious') {
        facts.push(
          L(
            '重病治療除了醫療費，休養期間的收入也會受影響',
            '重病治疗除了医疗费，休养期间的收入也会受影响',
            'Beyond medical bills, serious illness also affects income while recovering'
          )
        );
      }
      return {
        low: band[0],
        high: band[1],
        years: WHEN_YEARS[a.when],
        basis: L('自付的醫療及休養開支', '自付的医疗及休养开支', 'Out-of-pocket medical and recovery costs'),
        typical: L(
          '醫療開支隨年齡上升，越早預留，將來的壓力越小。',
          '医疗开支随年龄上升，越早预留，将来的压力越小。',
          'Medical costs rise with age; the earlier you set money aside, the less pressure later.'
        ),
        facts
      };
    }
  },
  {
    id: 'car',
    icon: '🚗',
    label: L('買車', '买车', 'Buying a car'),
    personas: ['parent', 'individual'],
    questions: [
      {
        id: 'type',
        question: L('想買哪一類車？', '想买哪一类车？', 'What kind of car?'),
        options: [
          { key: 'small', label: L('小型房車', '小型房车', 'Small car') },
          { key: 'mid', label: L('中型／電動車', '中型／电动车', 'Mid-size / electric') },
          { key: 'luxury', label: L('豪華房車', '豪华房车', 'Luxury car') }
        ]
      },
      WHEN,
      {
        id: 'parking',
        question: L('需要租車位嗎？', '需要租车位吗？', 'Do you need to rent a parking space?'),
        options: [
          { key: 'own', label: L('已有車位', '已有车位', 'Already have one') },
          { key: 'rent', label: L('需要租', '需要租', 'Need to rent') }
        ]
      }
    ],
    estimate: a => {
      const band = bandOf(
        { small: [20 * WAN, 30 * WAN], mid: [30 * WAN, 50 * WAN], luxury: [60 * WAN, 100 * WAN] },
        a.type
      );
      if (!band || WHEN_YEARS[a.when] === undefined) return null;
      const facts = [
        L(
          '每年養車（牌費、保險、油費或電費、維修）約 3–5 萬',
          '每年养车（牌费、保险、油费或电费、维修）约 3–5 万',
          'Running a car (licence, insurance, fuel or charging, repairs) costs about $30K–50K a year'
        )
      ];
      if (a.parking === 'rent') {
        facts.push(
          L(
            '租用車位每月約 3,000–5,000 元，一年約 4–6 萬',
            '租用车位每月约 3,000–5,000 元，一年约 4–6 万',
            'A rented space is about $3,000–5,000 a month, or $40K–60K a year'
          )
        );
      }
      return {
        low: band[0],
        high: band[1],
        years: WHEN_YEARS[a.when],
        basis: L(
          '車價，另加首年牌費及保險',
          '车价，另加首年牌费及保险',
          "The car's price, plus the first year's licence and insurance"
        ),
        typical: L(
          '除了車價，每年的養車開支往往更影響每月預算。',
          '除了车价，每年的养车开支往往更影响每月预算。',
          'Beyond the price, the yearly running costs often hit the monthly budget harder.'
        ),
        facts
      };
    }
  },
  {
    id: 'passive',
    icon: '💵',
    label: L('被動收入', '被动收入', 'Passive income'),
    personas: ['parent', 'individual'],
    questions: [
      {
        id: 'monthly',
        question: L(
          '希望每月有多少被動收入（以今天的物價計）？',
          '希望每月有多少被动收入（以今天的物价计）？',
          "How much passive income would you like a month (at today's prices)?"
        ),
        options: [
          { key: 'm5', label: L('約 5 千', '约 5 千', 'About $5K') },
          { key: 'm10', label: L('約 1 萬', '约 1 万', 'About $10K') },
          { key: 'm20', label: L('約 2 萬', '约 2 万', 'About $20K') }
        ]
      },
      WHEN,
      {
        id: 'source',
        question: L('想靠甚麼帶來收入？', '想靠什么带来收入？', 'Where would the income come from?'),
        options: [
          { key: 'dividend', label: L('收息', '收息', 'Dividends / interest') },
          { key: 'rent', label: L('收租', '收租', 'Rent') },
          { key: 'unsure', label: L('未決定', '未决定', 'Not decided') }
        ]
      }
    ],
    estimate: a => {
      const monthly = ({ m5: 0.5, m10: 1, m20: 2 } as Record<string, number>)[a.monthly];
      if (!monthly || WHEN_YEARS[a.when] === undefined) return null;
      const facts: Txt[] = [];
      if (a.source === 'dividend') {
        facts.push(
          L(
            '收息資產的價格和派息都會變動，並非保證',
            '收息资产的价格和派息都会变动，并非保证',
            'Prices and payouts of income assets both change and are not guaranteed'
          )
        );
      }
      if (a.source === 'rent') {
        facts.push(
          L(
            '收租要計算差餉、管理費、維修和空置期，實際回報通常比表面低',
            '收租要计算差饷、管理费、维修和空置期，实际回报通常比表面低',
            'Rental income must cover rates, management fees, repairs and vacancies, so the real return is usually lower than it looks'
          )
        );
      }
      // The capital that pays it at a 5% (low) to 4% (high) yield a year.
      return {
        low: (monthly * WAN * 12) / 0.05,
        high: (monthly * WAN * 12) / 0.04,
        years: WHEN_YEARS[a.when],
        basis: L(
          '以每年 4–5% 收益推算所需本金',
          '以每年 4–5% 收益推算所需本金',
          'The capital needed at a 4–5% yearly yield'
        ),
        typical: L(
          '被動收入通常要經過多年累積本金，越早開始越輕鬆。',
          '被动收入通常要经过多年累积本金，越早开始越轻松。',
          'Passive income usually takes years of building capital; the earlier you start, the easier it is.'
        ),
        facts
      };
    }
  }
];

const isTxt = (label: GoalDef['label']): label is Txt => 'en' in label;

export const goalLabel = (goal: GoalDef, persona: CoverPersona | undefined, lang: QuizLang): string =>
  isTxt(goal.label) ? goal.label[lang] : goal.label[persona || 'individual'][lang];

export const goalsFor = (persona?: CoverPersona) => GOALS.filter(g => g.personas.includes(persona || 'individual'));

export const SAVED_QUESTION: GoalQuestion = {
  id: 'saved',
  question: L(
    '為這些目標，你已經準備了多少？',
    '为这些目标，你已经准备了多少？',
    'How much have you already put aside for these goals?'
  ),
  options: [
    { key: 's0', label: L('還未開始', '还未开始', 'Not started yet') },
    { key: 's5', label: L('10 萬以下', '10 万以下', 'Under $100K') },
    { key: 's30', label: L('10–50 萬', '10–50 万', '$100K–500K') },
    { key: 's80', label: L('50 萬以上', '50 万以上', 'Over $500K') }
  ]
};
const SAVED_AMOUNT: Record<string, number> = { s0: 0, s5: 5 * WAN, s30: 30 * WAN, s80: 80 * WAN };

export const INCOME_QUESTION: GoalQuestion = {
  id: 'income',
  question: L(
    '你（或家庭）每月收入大約多少？',
    '你（或家庭）每月收入大约多少？',
    "Roughly what is your (or your family's) monthly income?"
  ),
  options: [
    { key: 'i2', label: L('2 萬以下', '2 万以下', 'Under $20K') },
    { key: 'i4', label: L('2–4 萬', '2–4 万', '$20K–40K') },
    { key: 'i6', label: L('4–6 萬', '4–6 万', '$40K–60K') },
    { key: 'i8', label: L('6 萬以上', '6 万以上', 'Over $60K') }
  ]
};
const INCOME_AMOUNT: Record<string, number> = { i2: 1.5 * WAN, i4: 3 * WAN, i6: 5 * WAN, i8: 8 * WAN };

export const MONTHLY_QUESTION: GoalQuestion = {
  id: 'monthly',
  question: L(
    '每月大約可以為這些目標存多少？',
    '每月大约可以为这些目标存多少？',
    'Roughly how much a month can you save for these goals?'
  ),
  options: [
    { key: 'c2', label: L('3,000 以下', '3,000 以下', 'Under 3,000') },
    { key: 'c5', label: L('3,000–8,000', '3,000–8,000', '3,000–8,000') },
    { key: 'c11', label: L('8,000–15,000', '8,000–15,000', '8,000–15,000') },
    { key: 'c20', label: L('15,000 以上', '15,000 以上', 'Over 15,000') }
  ]
};
const MONTHLY_AMOUNT: Record<string, number> = { c2: 2000, c5: 5500, c11: 11500, c20: 20000 };

/** The share of income commonly suggested for saving, shown beside the client's own. */
export const SUGGESTED_SAVING_RATE = 20;

/** Part 2's answers, stored beside part 1's. */
export type CoverGoalAnswers = {
  picked: string[];
  details: Record<string, Record<string, string>>;
  saved?: string;
  /** Asked since the benchmarks were added; absent on older records. */
  income?: string;
  monthly?: string;
};

// ── the arithmetic ────────────────────────────────────────────────────────────
const monthlyRate = DEMO_RETURN / 12;
const growth = (years: number) => Math.pow(1 + monthlyRate, years * 12);
/** Value in `years` of a monthly saving of 1. */
const annuityFactor = (years: number) => (growth(years) - 1) / monthlyRate;

/** Monthly saving that reaches `target` in `years`, after `saved` has grown. */
export const monthlyNeeded = (target: number, years: number, saved: number) => {
  if (years <= 0) return Math.max(0, target - saved);
  const remaining = target - saved * growth(years);
  return remaining <= 0 ? 0 : remaining / annuityFactor(years);
};

export type GoalPlanItem = {
  id: string;
  icon: string;
  label: string;
  basis: string;
  years: number;
  /** Today's prices. */
  todayLow: number;
  todayHigh: number;
  /** When it is needed, after inflation. */
  futureLow: number;
  futureHigh: number;
  monthlyLow: number;
  monthlyHigh: number;
  typical: string | null;
  facts: string[];
};

export type GoalPlan = {
  items: GoalPlanItem[];
  saved: number;
  capacity: number | null;
  income: number | null;
  /** Middle of the monthly range, all goals together. */
  totalMonthly: number;
  /** How much of the target the stated monthly saving covers, 0–100. Null without a capacity. */
  coveragePct: number | null;
  /** The stated monthly saving as a share of income. Null without both. */
  savingRatePct: number | null;
  /** The monthly saving the goals need, as a share of income. Null without an income. */
  neededRatePct: number | null;
};

/** The plan, worded in `lang`. */
export const planGoals = (
  answers: CoverGoalAnswers | undefined,
  persona: CoverPersona | undefined,
  lang: QuizLang
): GoalPlan | null => {
  if (!answers || !answers.picked.length) return null;
  const saved = SAVED_AMOUNT[answers.saved || ''] ?? 0;
  const capacity = answers.monthly ? MONTHLY_AMOUNT[answers.monthly] ?? null : null;
  const income = answers.income ? INCOME_AMOUNT[answers.income] ?? null : null;
  const goals = answers.picked.map(id => GOALS.find(g => g.id === id)).filter((g): g is GoalDef => Boolean(g));
  // Savings already put aside are shared evenly between the goals.
  const savedEach = goals.length ? saved / goals.length : 0;

  const items: GoalPlanItem[] = [];
  goals.forEach(goal => {
    const estimate = goal.estimate(answers.details[goal.id] || {}, { monthlyIncome: income });
    if (!estimate) return;
    const inflate = Math.pow(1 + DEMO_INFLATION, estimate.years);
    const futureLow = estimate.low * inflate;
    const futureHigh = estimate.high * inflate;
    items.push({
      id: goal.id,
      icon: goal.icon,
      label: goalLabel(goal, persona, lang),
      basis: estimate.basis[lang],
      years: estimate.years,
      todayLow: estimate.low,
      todayHigh: estimate.high,
      futureLow,
      futureHigh,
      monthlyLow: monthlyNeeded(futureLow, estimate.years, savedEach),
      monthlyHigh: monthlyNeeded(futureHigh, estimate.years, savedEach),
      typical: estimate.typical ? estimate.typical[lang] : null,
      facts: (estimate.facts || []).filter(Boolean).map(fact => fact[lang])
    });
  });
  if (!items.length) return null;

  const totalMonthly = items.reduce((sum, i) => sum + (i.monthlyLow + i.monthlyHigh) / 2, 0);
  const coveragePct =
    capacity === null ? null : totalMonthly <= 0 ? 100 : Math.min(100, Math.round((capacity / totalMonthly) * 100));
  const savingRatePct = capacity !== null && income ? Math.round((capacity / income) * 100) : null;
  const neededRatePct = income ? Math.round((totalMonthly / income) * 100) : null;
  return { items, saved, capacity, income, totalMonthly, coveragePct, savingRatePct, neededRatePct };
};
